import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';

// PRD §8 UGC 四件套审核：处理用户举报。
// GET /v1/admin/community/reports → 列待处理
// POST /v1/admin/community/reports/:id/resolve body{decision, note} → 处理

type Report = {
  id: string;
  reporterId?: string;
  targetType?: string;
  targetId?: string;
  reason?: string;
  status?: string;
  createdAt?: string;
};

export default function ReportsQueuePage() {
  const [list, setList] = useState<Report[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // 正在处理的 report id

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await api<{ items?: Report[] }>(
        '/admin/community/reports?limit=100',
      );
      setList(r.items ?? []);
    } catch (e) {
      setErr((e as ApiError).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id: string, decision: 'accept' | 'reject') => {
    const noteRaw =
      decision === 'accept'
        ? prompt('处理说明（可空，将记入 audit log）：', '内容违反社区规范')
        : prompt('驳回原因：', '不构成违规');
    if (noteRaw === null) return; // 用户 cancel
    setBusy(id);
    try {
      await api(`/admin/community/reports/${id}/resolve`, {
        method: 'POST',
        json: { decision, note: noteRaw },
      });
      await load();
    } catch (e) {
      alert(`处理失败：${(e as ApiError).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div style={S.header}>
        <h1 style={{ margin: 0 }}>举报队列</h1>
        <button style={S.refresh} onClick={load}>刷新</button>
      </div>

      {err && <div style={S.err}>加载失败：{err}</div>}

      {list === null && <div style={S.loading}>加载中…</div>}
      {list && list.length === 0 && (
        <div style={S.empty}>🎉 队列为空，没有待处理举报。</div>
      )}

      {list && list.length > 0 && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>举报时间</th>
              <th style={S.th}>举报人</th>
              <th style={S.th}>目标</th>
              <th style={S.th}>原因</th>
              <th style={S.th}>状态</th>
              <th style={S.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id}>
                <td style={S.td}>{r.createdAt?.slice(0, 16) ?? '-'}</td>
                <td style={S.td}>{r.reporterId ?? '-'}</td>
                <td style={S.td}>
                  <code>{r.targetType}/{r.targetId}</code>
                </td>
                <td style={S.td}>{r.reason ?? '-'}</td>
                <td style={S.td}>
                  <Status status={r.status ?? 'open'} />
                </td>
                <td style={S.td}>
                  <button
                    style={S.btnAccept}
                    disabled={busy === r.id}
                    onClick={() => resolve(r.id, 'accept')}
                  >
                    {busy === r.id ? '...' : '受理'}
                  </button>
                  <button
                    style={S.btnReject}
                    disabled={busy === r.id}
                    onClick={() => resolve(r.id, 'reject')}
                  >
                    驳回
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Status({ status }: { status: string }) {
  const color =
    status === 'open' ? '#f5a623' : status === 'accepted' ? '#d33' : '#888';
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 4,
        background: `${color}22`,
        color,
        fontSize: 12,
      }}
    >
      {status}
    </span>
  );
}

const S: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  refresh: {
    padding: '6px 14px',
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: 6,
    cursor: 'pointer',
  },
  err: {
    padding: 12,
    background: '#fee',
    color: '#d33',
    borderRadius: 6,
    marginBottom: 16,
  },
  loading: { color: '#888', padding: 24 },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: '#888',
    background: 'white',
    borderRadius: 8,
  },
  table: {
    width: '100%',
    background: 'white',
    borderRadius: 8,
    borderCollapse: 'collapse',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    fontSize: 12,
    color: '#666',
    background: '#fafafa',
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    borderBottom: '1px solid #f4f4f4',
  },
  btnAccept: {
    padding: '4px 10px',
    marginRight: 6,
    background: '#1f9d8f',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
  },
  btnReject: {
    padding: '4px 10px',
    background: 'white',
    color: '#666',
    border: '1px solid #ccc',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
  },
};
